// Ícones SVG inline da landing `/`.
//
// Por que não Remix (`ri-*`) aqui: a fonte de ícones carrega de forma ASSÍNCRONA
// (index.html, 14/10/2026) para não travar a primeira pintura. Tudo o que fica
// acima da dobra (cabeçalho, hero, celular, barra do atendente) precisa aparecer
// completo na hora — por isso é SVG inline, sem depender de rede. As seções
// abaixo da dobra seguem usando Remix normalmente (quando a pessoa chega lá, a
// fonte já carregou). NUNCA lucide-react neste projeto.
import type { SVGProps } from "react";

const PATHS = {
  check: "M5 12.5l4.5 4.5L19 7.5",
  arrowRight: "M5 12h14M13 6l6 6-6 6",
  arrowDown: "M12 5v14M6 13l6 6 6-6",
  mic: "M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3ZM5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21",
  video: "M4 7.5A1.5 1.5 0 0 1 5.5 6h8A1.5 1.5 0 0 1 15 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 4 16.5v-9ZM15 10.5l5-3v9l-5-3",
  chat: "M4.5 5.5h15a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4.5 3.5V6.5a1 1 0 0 1 1-1Z",
  receita: "M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1ZM14 3v5h5M9.5 13h6M9.5 16.5h4",
  shield: "M12 3l7 3v5.5c0 4.4-2.9 7.9-7 9.5-4.1-1.6-7-5.1-7-9.5V6l7-3ZM9 12l2.2 2.2L15.5 10",
  atestado: "M7 3h10a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1ZM9.5 8h5M9.5 11.5h5M9.5 15h3",
  browser: "M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1ZM3 9h18M6.5 7h.01M9 7h.01",
  stethoscope: "M6 3v5a4 4 0 0 0 8 0V3M10 12v2.5a5 5 0 0 0 10 0V12M20 12a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z",
  lock: "M7 11V8a5 5 0 0 1 10 0v3M6 11h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z",
} as const;

export type IconName = keyof typeof PATHS;

export function Ico({ n, className = "w-5 h-5", strokeWidth = 2, ...rest }: { n: IconName } & SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" focusable="false" {...rest}>
      <path d={PATHS[n]} />
    </svg>
  );
}

/** Raio da marca (23/10/2026, dono: "o raio precisa ser completo e redondo nas pontas").
 *  Mesmo desenho do favicon (public/icons) e do .ri-flashlight-* (src/index.css):
 *  preenchido, com as pontas arredondadas pelo contorno de junção redonda. */
export const BOLT_D = "M14.04 2.38 12.79 10.05H18.43L9.98 21.67 11.17 13.94H5.51Z";
export function Bolt({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path d={BOLT_D} fill="currentColor" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}

/** Telefone de desligar (preenchido). */
export function HangUp({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12 9.5c-3.3 0-6.3.9-8.5 2.6-.6.5-.8 1.3-.4 2l1.1 1.9c.4.7 1.3.9 2 .5l2-1.2c.5-.3.8-.9.7-1.5l-.2-1.2c1-.4 2.1-.5 3.3-.5s2.3.1 3.3.5l-.2 1.2c-.1.6.2 1.2.7 1.5l2 1.2c.7.4 1.6.2 2-.5l1.1-1.9c.4-.7.2-1.5-.4-2-2.2-1.7-5.2-2.6-8.5-2.6Z" />
    </svg>
  );
}

/** Logo do WhatsApp (balão + fone), desenhado à mão — sem pacote de ícones. */
export function WhatsGlyph({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M12 2.2a9.8 9.8 0 0 0-8.4 14.8L2.3 21.7l4.8-1.3A9.8 9.8 0 1 0 12 2.2Zm0 17.8a8 8 0 0 1-4.1-1.1l-.3-.2-2.8.8.8-2.7-.2-.3A8 8 0 1 1 12 20Z" />
      <path fill="currentColor" d="M16.4 14.1c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1-.7-.3-1.4-.7-2-1.3-.5-.5-1-1.1-1.3-1.7-.1-.2 0-.4.1-.5l.4-.4.2-.4v-.4l-.7-1.7c-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.6.6-.9 1.4-.9 2.2.1.9.4 1.8 1 2.6 1 1.5 2.4 2.7 4 3.4.4.2.9.4 1.4.5.5.2 1 .2 1.5.1.6-.1 1.3-.6 1.6-1.1.2-.4.2-.8.1-1.2l-.4-.2Z" />
    </svg>
  );
}
